import { useEffect, useState } from 'react';
import { ContentfulRichText } from '@wbk/contentful';
import { type WebSection } from '@wbk/contentful/api';
import { Typography } from '@wbk/ui';

const HeroSection = ({
  section,
  introFinished,
}: {
  section: WebSection;
  introFinished: boolean;
}) => {
  const titleArr = section?.title?.split(' ') || [];
  const titleFirstWord = titleArr.shift() || '';
  const titleRest = titleArr;
  const [time, setTime] = useState<number>(0);
  useEffect(() => {
    if (!introFinished) return;

    const introVideo = document.getElementById('intro-video') as HTMLVideoElement | null;
    if (introVideo) {
      setTime(introVideo.currentTime);
    }

    const hash = window.location.hash;

    if (hash === '#booklet-section') {
      window.scrollTo({
        top: document.getElementById('booklet-section')
          ? document.getElementById('booklet-section')!.offsetTop - 200
          : 0,
        behavior: 'smooth',
      });
    }
  }, [introFinished]);
  return (
    <>
      <div className='relative h-dvh md:h-screen'>
        <div className='absolute inset-0'>
          <div className='background-video pointer-events-none absolute inset-0 mx-auto h-full bg-gradient-to-b from-transparent via-black/40 to-black/80 mix-blend-multiply' />
          <video
            className='background-video no-controls mx-auto h-full object-cover'
            src={section?.sectionBackground?.url}
            autoPlay
            muted
            loop
            playsInline
            // start time
            defaultValue={time || 0}
          />
        </div>
        <div className='container relative h-full w-full'>
          <div
            className='absolute top-[70%] flex -translate-y-1/2 transform flex-col gap-6 px-6 sm:gap-10 sm:px-8 xl:top-[65%]'
            style={{
              mixBlendMode: 'difference',
            }}
          >
            <div className='space-y-6 lg:space-y-10 rtl:space-y-4'>
              <Typography
                as='h5'
                variant='body-S'
                className='sm:text-body-M 2xl:text-body-L m-0 font-bold leading-none'
              >
                {section?.subtitle}
              </Typography>

              <div className='-ml-2 flex items-center gap-20'>
                <Typography
                  as='h1'
                  variant='display-S'
                  className='2xl:text-display-L font-gellix rtl:font-expo-arabic m-0 font-extrabold uppercase !leading-[56%] mix-blend-difference md:text-[150px] xl:text-[180px] ltr:tracking-tighter rtl:!leading-[120%] rtl:md:text-[175px] rtl:xl:text-[220px] rtl:2xl:text-[250px]'
                  style={{
                    willChange: 'transform',
                  }}
                >
                  {titleFirstWord}
                </Typography>
                <div className='hidden max-w-md space-y-2 text-[#A1A1AA] lg:block'>
                  <Typography as='div' variant='body-S' className='xl:text-body-M 2xl:text-body-L'>
                    {section?.collapsableDescription && (
                      <ContentfulRichText description={section?.collapsableDescription} />
                    )}
                  </Typography>
                </div>
              </div>
            </div>
            <div className='-ml-2 flex flex-col gap-6 sm:gap-10 lg:flex-row lg:items-center rtl:sm:gap-12'>
              <Typography
                as='h2'
                variant='display-S'
                className='2xl:text-display-L rtl:!font-expo-arabic !font-gellix bg-clip-text font-extrabold uppercase !leading-[56%] tracking-tighter mix-blend-difference md:text-[150px] xl:text-[180px] rtl:md:text-[160px] rtl:xl:text-[200px] rtl:2xl:text-[230px]'
              >
                {titleRest?.[0]}
              </Typography>
              <Typography
                as='h2'
                variant='display-S'
                className='2xl:text-display-L !font-gellix mt-4 flex flex-nowrap font-extrabold uppercase !leading-[56%] sm:-space-x-2 sm:tracking-[-15px] md:mt-0 md:text-[180px] lg:text-[150px] rtl:flex-row-reverse rtl:justify-end rtl:md:text-[160px] rtl:xl:text-[200px]'
              >
                <span className='font-light tracking-[-6px] text-[#ACACAC] lg:tracking-[-16px]'>
                  20
                </span>
                <span className='text-primary tracking-[-6px] lg:tracking-[-16px]'>25</span>
              </Typography>
            </div>
            <div className='max-w-md space-y-2 text-[#A1A1AA] lg:hidden'>
              <Typography as='div' variant='body-S' className='sm:text-body-M 2xl:text-body-L'>
                {section?.collapsableDescription && (
                  <ContentfulRichText description={section?.collapsableDescription} />
                )}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HeroSection;
